import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { getMapSpots } from '../../api/spaceApi';
import { selectFieldSx, dropdownMenuProps } from '../../utils/dropdownStyles';

/**
 * Reusable map spot selector — dropdown only.
 * Map spots are managed via Unity admin APIs; no creation UI here.
 *
 * Props:
 *   value       — selected map spot id (number | '')
 *   onChange    — callback(id: number, spot: MapSpotDto)
 *   required    — show asterisk + validation helper
 *   disabledIds — Set of map spot ids to show as disabled (already in use)
 */
const MapSpotSelector = ({ value, onChange, required = true, disabledIds = new Set() }) => {
  const [spots,   setSpots]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMapSpots()
      .then((res) => setSpots(Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (e) => {
    const val = e.target.value;
    const spot = spots.find((s) => s.id === val);
    onChange(val, spot);
  };

  return (
    <Box>
      <FormControl fullWidth>
        <InputLabel>
          Map Spot {required && <span style={{ color: '#d32f2f' }}>*</span>}
        </InputLabel>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading map spots…</Typography>
          </Box>
        ) : (
          <Select
            label="Map Spot"
            value={value || ''}
            onChange={handleSelect}
            sx={selectFieldSx}
            MenuProps={dropdownMenuProps}
          >
            {spots.map((s) => {
              const inUse = disabledIds.has(s.id);
              return (
                <MenuItem
                  key={s.id}
                  value={s.id}
                  disabled={inUse}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}
                >
                  <span>{s.label}</span>
                  {inUse && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
                    >
                      Used
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </Select>
        )}
        {required && !value && (
          <FormHelperText>Required</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default MapSpotSelector;
